import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { useStreams } from "../hooks/useStreams";
import { Loader2 } from "lucide-react";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import DeveloperKeys from "../components/DeveloperKeys";
import {
  useAdvancedSettings,
  fetchExpirySettings,
  saveExpirySettings,
} from "../utils/advancedSettings";

const DEFAULT_MESSAGE = "This event has ended and its captions are no longer available.";

const Toggle: React.FC<{ id: string; label: string; help: string; checked: boolean; onChange: (v: boolean) => void }> = ({ id, label, help, checked, onChange }) => (
  <div className="flex items-start justify-between gap-6 py-4">
    <div>
      <label htmlFor={id} className="font-bold text-slate-900">{label}</label>
      <p className="text-slate-600 text-sm mt-1">{help}</p>
    </div>
    <input id={id} type="checkbox" className="mt-1 h-6 w-6 shrink-0 accent-blue-600" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </div>
);

const AdvancedSettings: React.FC = () => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const { settings, save } = useAdvancedSettings(user?.uid);

  const [streamName, setStreamName] = useState("");
  const [hours, setHours] = useState("6");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { selected } = useStreams();
  useEffect(() => {
    if (selected) setStreamName(selected.name);
  }, [selected]);

  useEffect(() => {
    if (!user || !streamName) return;
    fetchExpirySettings(streamName, user.uid)
      .then((s) => {
        setHours(String(s.expiryHours));
        setMessage(s.expiredMessage);
        setExpiresAt(s.expiresAt);
      })
      // The stream is created in the API by the first post from the app; until then the defaults apply.
      .catch(() => setStatus("These settings become available after you start your first stream from the app."));
  }, [user, streamName]);

  const onSaveExpiry = async () => {
    if (!user) return;
    const parsed = Number(hours);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 8760) {
      setStatus("Enter a whole number of hours between 0 and 8760.");
      return;
    }
    setSaving(true);
    try {
      await saveExpirySettings(streamName, user.uid, parsed, message);
      setStatus("Saved.");
      fetchExpirySettings(streamName, user.uid).then((s) => setExpiresAt(s.expiresAt)).catch(() => undefined);
    } catch (err) {
      setStatus("Could not save. Start your stream from the app once, then try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="advanced-settings" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <header>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Advanced Settings</h1>
              <p className="text-slate-600 mt-1 text-lg">Options for events with special needs. Everything here is off by default.</p>
            </header>

            <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 divide-y divide-slate-100" aria-labelledby="features-heading">
              <h2 id="features-heading" className="text-xl font-bold text-slate-900 pb-3">Features</h2>
              <Toggle
                id="secure-qr"
                label="Show secure QR code"
                help="Shows a second QR code under your stream's QR code on the Overview page. It is for sessions where you check “Require authentication” in the app, so only people you give this code to can read the captions."
                checked={settings.secureQr}
                onChange={(v) => save({ secureQr: v })}
              />
              <Toggle
                id="advanced-history"
                label="Enable advanced history actions"
                help="In Transcript History, adds a title and a note for each transcript, and Hide / Unhide to keep a transcript out of public views."
                checked={settings.advancedHistory}
                onChange={(v) => save({ advancedHistory: v })}
              />
            </section>

            <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" aria-labelledby="expiry-heading">
              <h2 id="expiry-heading" className="text-xl font-bold text-slate-900">Captions availability</h2>
              <p className="text-slate-600 text-sm mt-1">After your stream ends, attendees can keep reading the captions for this long. Then they see your message instead. You can still read your own transcripts in Transcript History.</p>

              <div className="mt-5 space-y-5">
                <div>
                  <label htmlFor="expiry-hours" className="font-bold text-slate-900 block">Hide captions this many hours after the stream ends</label>
                  <input
                    id="expiry-hours"
                    inputMode="numeric"
                    className="mt-2 w-32 bg-slate-50 border border-slate-300 rounded-xl py-2 px-3"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                  />
                  <p className="text-slate-600 text-sm mt-1">Default 6. Use 0 to hide them as soon as the stream ends.</p>
                </div>
                <div>
                  <label htmlFor="expired-message" className="font-bold text-slate-900 block">Message shown after captions are hidden</label>
                  <textarea
                    id="expired-message"
                    rows={3}
                    className="mt-2 w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <p className="text-slate-600 text-sm mt-1">For example: “The captions for this event are no longer available. Please email events@example.org with any questions.” Leave it empty to use the default.</p>
                </div>
                {expiresAt && (
                  <p className="text-slate-700 text-sm" aria-live="polite">
                    Captions {new Date(expiresAt) > new Date() ? "will be hidden at" : "were hidden at"}{" "}
                    <strong>{new Date(expiresAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</strong> (your device's time zone).
                  </p>
                )}
                <div className="flex items-center gap-4">
                  <button onClick={onSaveExpiry} disabled={saving || !streamName} className="btn-primary">
                    {saving ? "Saving..." : "Save"}
                  </button>
                  {status && <span className="text-sm text-slate-700" role="status">{status}</span>}
                </div>
              </div>
            </section>

            {user && <DeveloperKeys getToken={() => user.getIdToken()} />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvancedSettings;
